import type {CSSProperties} from 'react';
import {WEDGE_SAVED} from '@territory/tokens';
import {radius, TOUCH_MIN, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeSavedSheetLook = 'saved-sample-toggle' | 'saved-sample-restore' | 'saved-restore-all' | 'saved-sheet-status' | 'saved-sheet-status-on' | 'saved-sheet-delete' | 'saved-sheet-cancel';

const SHEET_LOOKS = new Set<string>(['saved-sample-toggle', 'saved-sample-restore', 'saved-restore-all', 'saved-sheet-status', 'saved-sheet-status-on', 'saved-sheet-delete', 'saved-sheet-cancel']);
export const isWedgeSavedSheetLook = (look: string): look is WedgeSavedSheetLook => SHEET_LOOKS.has(look);

/* Released Saved sample briefs and status and delete sheets: seven native controls, all in the Wedge font. The sample toggle is the full-width
   header row of the sample section: a flex row with the title group left and the chevron right, bare, with 0 4px 10px of padding. Restore
   hidden samples is a bare inline flex text button (5px gap, no padding, 11.5px semibold accent type); Restore samples is a surface pill with
   a 1px card border, a 6px gap, 8px by 16px padding and 12.5px semibold secondary type. Both take the tap highlight off and the touch action at
   manipulation. A status row is a full-width left-aligned flex row: 10px gap, 12px by 13px padding, an 11px radius, a 44px minimum height, a
   0.5px border and 14px semibold label type; idle it takes the elevated fill and the separator border, chosen the soft accent fill and a
   transparent border. Delete permanently is a full-width 50px block with a 14px radius, the red fill and the fixed white ink
   WEDGE_SAVED.staleInk (white in every theme); Cancel is a full-width 46px block with a 14px radius, a transparent fill, a 0.5px
   separator border and 15px semibold secondary type. The caller owns the glyphs, the labels, the margins and the handlers. The toggle and Restore
   hidden samples draw no transparent target (wedgeSettingsDrawsHit): the toggle region would reach 8px into the last saved row above it (a 28px box), and the
   18px high Restore hidden text sits flush under the toggle, where a 44px region would reach 13px into it. Restore samples and the sheet looks keep
   the 44px region, which stays inside the padding round the pill and equals the box of the three full-width blocks. */
const ROW_RADIUS = 11, ROW_PAD = '12px 13px', ROW_GAP = 10, STATUS_FONT = 14;

export function wedgeSavedSheetStyle(look: WedgeSavedSheetLook): CSSProperties {
  const r = wedgeExactRole, fontFamily = r('font');
  const tap = {WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation'} as const;
  if (look === 'saved-sample-toggle') return {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 4px 10px',
    background: 'transparent', border: 0, cursor: 'pointer', fontFamily,
  };
  if (look === 'saved-sample-restore') return {
    display: 'inline-flex', alignItems: 'center', gap: 5, background: 'transparent', border: 0, cursor: 'pointer', padding: 0,
    fontFamily, fontSize: 11.5, fontWeight: weight.semibold, color: r('accent'), ...tap,
  };
  if (look === 'saved-restore-all') return {
    display: 'inline-flex', alignItems: 'center', gap: 6, background: r('surface'), border: `1px solid ${r('cardBorder')}`, borderRadius: radius.pill,
    cursor: 'pointer', padding: '8px 16px', fontFamily, fontSize: 12.5, fontWeight: weight.semibold, color: r('labelSec'), ...tap,
  };
  if (look === 'saved-sheet-status' || look === 'saved-sheet-status-on') {
    const on = look === 'saved-sheet-status-on';
    return {
      display: 'flex', alignItems: 'center', gap: ROW_GAP, padding: ROW_PAD, borderRadius: ROW_RADIUS, minHeight: TOUCH_MIN,
      background: on ? r('accentSoft') : r('bgElev'), color: r('label'), border: `0.5px solid ${on ? 'transparent' : r('separator')}`,
      cursor: 'pointer', fontFamily, fontSize: STATUS_FONT, fontWeight: weight.semibold, textAlign: 'left', ...tap,
    };
  }
  if (look === 'saved-sheet-delete') return {
    width: '100%', height: 50, borderRadius: radius.xl, background: r('red'), color: WEDGE_SAVED.staleInk, border: 0,
    cursor: 'pointer', fontSize: 16, fontWeight: weight.semibold, fontFamily,
  };
  return {
    display: 'block', width: '100%', height: 46, borderRadius: radius.xl, background: 'transparent', color: r('labelSec'),
    border: `0.5px solid ${r('separator')}`, cursor: 'pointer', fontSize: 15, fontWeight: weight.semibold, fontFamily,
  };
}
